import { Loader, Modal } from '@mantine/core';
import { useTranslation } from 'react-i18next';

import { Button, InputField, TagsInput } from '../../../../../../../components/Atoms';
import type { InstanceNameErrorKey } from '../hooks/useEditProfileForm';

interface EditProfileModalProps {
    opened: boolean;
    instanceNameValue: string;
    instanceNameError: InstanceNameErrorKey;
    instanceTagsValue: string[];
    isSaving: boolean;
    onClose: () => void;
    onNameChange: (value: string) => void;
    onTagsChange: (tags: string[]) => void;
    onSave: () => void;
}

export default function EditProfileModal({
    opened,
    instanceNameValue,
    instanceNameError,
    instanceTagsValue,
    isSaving,
    onClose,
    onNameChange,
    onTagsChange,
    onSave,
}: EditProfileModalProps) {
    const { t } = useTranslation('cloudCompute');
    const errorText = instanceNameError ? t(`instanceNameErrors.${instanceNameError}`) : '';

    return (
        <Modal
            size="sm"
            opened={opened}
            onClose={onClose}
            withCloseButton={false}
            overlayProps={{
                backgroundOpacity: 0.55,
                blur: 3,
            }}
            transitionProps={{ transition: 'fade', duration: 128, timingFunction: 'linear' }}
            centered
        >
            <div className="flex flex-col justify-start items-start gap-4">
                <div className="flex flex-col justify-start items-start">
                    <Modal.Title>
                        <span className="text-base font-bold">{t('editProfileModal.title')}</span>
                    </Modal.Title>
                    <p className="text-sm text-fg/68">{t('editProfileModal.subtitle')}</p>
                </div>
                <div className="w-full flex flex-col justify-start items-start gap-6">
                    <InputField
                        label={t('editProfileModal.instanceName')}
                        description={t('editProfileModal.instanceNameDescription')}
                        placeholder={t('editProfileModal.instanceNamePlaceholder')}
                        value={instanceNameValue}
                        onChange={(e) => onNameChange(e.target.value)}
                        error={errorText}
                    />
                    <TagsInput
                        label={t('editProfileModal.instanceTags')}
                        description={t('editProfileModal.instanceTagsDescription')}
                        placeholder={t('editProfileModal.instanceTagsPlaceholder')}
                        value={instanceTagsValue}
                        onChange={onTagsChange}
                    />

                    <div className="w-full flex flex-col justify-start items-start gap-2.5">
                        <Button
                            width="100%"
                            onClick={onSave}
                            disabled={!!instanceNameError || isSaving}
                        >
                            {isSaving ? (
                                <div className="flex items-center gap-2">
                                    <Loader size={16} color="white" />
                                    <span>{t('editProfileModal.saving')}</span>
                                </div>
                            ) : (
                                t('editProfileModal.saveChanges')
                            )}
                        </Button>
                        <Button
                            width="100%"
                            variant="secondary"
                            onClick={onClose}
                            disabled={isSaving}
                        >
                            {t('editProfileModal.cancel')}
                        </Button>
                    </div>
                </div>
            </div>
        </Modal>
    );
}
